import type { MergedMarketRecord } from '@/types/market';
import type { GeoInsights } from '@/types/insights';
import { round } from '@/utils/numbers';

/**
 * Turns numeric scores into plain-language sentences. This is the
 * "interpretation engine, not copy engine" layer: every sentence is
 * generated from OUR derived scores/z-scores, and any raw published
 * figure we quote is explicitly attributed to its source label -- we never
 * imply we collected or own the underlying data ourselves.
 */
export function buildExplanations(record: MergedMarketRecord, insights: GeoInsights): string[] {
  const row = record.resolved;
  const lines: string[] = [];
  const sources = insights.contributingSources.join(', ') || 'no source';

  // Headline temperature line
  switch (insights.hotColdScore.label) {
    case 'hot':
      lines.push(
        `${record.geoName} County is running hot relative to the rest of the covered map, driven by shorter time on market and tighter inventory (based on ${sources}).`,
      );
      break;
    case 'warming':
      lines.push(`${record.geoName} County is trending warmer than the statewide average this period (based on ${sources}).`);
      break;
    case 'cooling':
      lines.push(`${record.geoName} County is trending cooler than the statewide average this period (based on ${sources}).`);
      break;
    case 'cold':
      lines.push(
        `${record.geoName} County is running cold relative to the rest of the covered map, with longer time on market and looser inventory (based on ${sources}).`,
      );
      break;
    case 'neutral':
      lines.push(`${record.geoName} County looks roughly in line with the statewide average this period (based on ${sources}).`);
      break;
    default:
      lines.push(`Not enough data was available to score ${record.geoName} County's overall temperature this period.`);
  }

  // Sell-speed shift
  if (row.medianDaysOnMarket !== undefined && row.medianDaysOnMarketPrevPeriod !== undefined) {
    const domDelta = row.medianDaysOnMarket - row.medianDaysOnMarketPrevPeriod;
    if (Math.abs(domDelta) >= 1) {
      lines.push(
        domDelta < 0
          ? `Median days on market fell to ${row.medianDaysOnMarket} (from ${row.medianDaysOnMarketPrevPeriod}), suggesting homes are moving faster.`
          : `Median days on market rose to ${row.medianDaysOnMarket} (from ${row.medianDaysOnMarketPrevPeriod}), suggesting homes are sitting longer.`,
      );
    }
  }

  // Inventory pressure
  if (insights.inventoryPressure.label !== 'insufficient-data') {
    if (insights.inventoryPressure.value >= 60) {
      lines.push('Inventory is building here, which typically gives buyers more room to negotiate.');
    } else if (insights.inventoryPressure.value <= 40) {
      lines.push('Inventory is tightening here, which typically favors sellers.');
    }
  }

  // Opportunity score
  if (insights.opportunityScore.value >= 65) {
    lines.push(
      `Opportunity score is elevated (${round(insights.opportunityScore.value, 0)}/100) -- more price reductions and slower turnover than most of the covered map right now.`,
    );
  }

  // Cooling risk
  if (insights.coolingRisk.value >= 65) {
    lines.push(
      `Cooling-risk signal is elevated (${round(insights.coolingRisk.value, 0)}/100) -- this period's trend lines (days on market, inventory, price growth) are shifting toward a slower market.`,
    );
  }

  // Buyer competition
  if (insights.buyerCompetition.value >= 65) {
    lines.push(`Buyer competition looks elevated (${round(insights.buyerCompetition.value, 0)}/100) -- listings are moving quickly with few price cuts.`);
  }

  // Confidence / data-quality caveat
  if (insights.confidenceScore.value < 50) {
    lines.push(
      'Confidence in this read is limited -- fewer corroborating sources or incomplete fields were available for this geography this period.',
    );
  }
  if (insights.isSampleData) {
    lines.push('This period is showing bundled SAMPLE data, not a live published report -- see the freshness badge for details.');
  }

  return lines;
}
